import React from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { useLocation } from 'react-router-dom';
import { useFlags } from '@flagsmith/flagsmith/react';

import { Flex, FlexColumn } from 'src/components/flex';
import Responsive from 'src/components/responsive/Responsive';
import { isManualAway, isManualOnline } from 'src/api/users-api';
import { useLoggedInUser } from 'src/services/api';
import { EFeatureFlag, FORCE_TEAM_AVAILABILITY } from 'src/utils/feature-flags';
import AwayBanner from 'src/components/layout/landlord/AwayBanner';
import OnlineOutOfHoursBanner from 'src/components/layout/landlord/OnlineOutOfHoursBanner';
import DesktopNavigationSidebar from 'src/components/layout/landlord/DesktopNavigationSidebar';
import FacebookIntegrationAlert from 'src/components/layout/landlord/FacebookIntegrationAlert';
import MaintenanceWindowAlert from 'src/components/layout/landlord/MaintenanceWindowAlert';
import HelpSidebar from 'src/components/layout/landlord/help-sidebar/HelpSidebar';
import LandlordLayoutProvider, { useLandlordLayoutContext } from 'src/components/layout/landlord/LandlordLayoutProvider';
import MobileAppBar from 'src/components/layout/landlord/MobileAppBar';
import MobileNavigationDrawer from 'src/components/layout/landlord/MobileNavigationDrawer';
import UnmatchedListingAlert from 'src/components/layout/landlord/UnmatchedListingAlert';
import SentryErrorBoundary from 'src/components/sentry-error-boundary';
import VacantPropertiesAlert from 'src/components/layout/landlord/VacantPropertiesAlert';
import NotificationAlert from 'src/pages/landlord/conversations/components/NotificationAlert';

type Props = {
    isLoading?: boolean;
    children?: React.ReactNode;
}

function LandlordLayoutContent({ isLoading, children }: Props) {
    const { isDrawerOpen, setDrawerOpen } = useLandlordLayoutContext();
    const { pathname } = useLocation();
    const flags = useFlags([EFeatureFlag.TEAM_AVAILABILITY]);
    const { data: loggedInUser } = useLoggedInUser();
    const isAvailabilityEnabled = Boolean(flags.team_availability?.enabled) || FORCE_TEAM_AVAILABILITY;
    const isAway = isAvailabilityEnabled && isManualAway(loggedInUser);
    const isOnlineOutOfHours = isAvailabilityEnabled && isManualOnline(loggedInUser);

    const handleDrawerOpen = () => {
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const hideUnmatchedAndVacantAlerts =
        pathname === '/landlord/inner/unmatched-listings' ||
        pathname.startsWith('/landlord/inner/messages/from-lead') ||
        (pathname.startsWith('/landlord/inner/playbooks') && pathname !== '/landlord/inner/playbooks');

    return (
        <>
            <FlexColumn
                minHeight="100vh"
                rowGap={0}
                columnGap={0}
            >
                <Responsive mobile>
                    <div>
                        <MobileAppBar openNavigationDrawer={handleDrawerOpen} />
                    </div>
                </Responsive>

                <Flex
                    flex={1}
                    minHeight="100%"
                    bgcolor="var(--background-color-beige)"
                    columnGap={0}
                    overflow="hidden"
                >
                    <DesktopNavigationSidebar onNavigationChange={handleDrawerClose} />

                    <SentryErrorBoundary>
                        <FlexColumn position="relative" rowGap={0} sx={{ flex: 1, overflow: 'hidden' }}>
                            {isLoading && (
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    width="100%"
                                >
                                    <LinearProgress />
                                </Box>
                            )}
                            {!isLoading && (
                                <>
                                    {isAway && <AwayBanner />}
                                    {isOnlineOutOfHours && <OnlineOutOfHoursBanner />}
                                    <FacebookIntegrationAlert />
                                    {!hideUnmatchedAndVacantAlerts && <UnmatchedListingAlert />}
                                    <MaintenanceWindowAlert />
                                    {children}
                                </>
                            )}
                        </FlexColumn>
                    </SentryErrorBoundary>
                </Flex>

                {isDrawerOpen && (
                    <Responsive mobile>
                        <div>
                            <MobileNavigationDrawer closeNavigationDrawer={handleDrawerClose} />
                        </div>
                    </Responsive>
                )}
            </FlexColumn>

            {!isLoading && (
                <>
                    <div className="noPrint"><HelpSidebar /></div>
                    <Box
                        className="noPrint"
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            zIndex: 1300,
                            display: { xs: 'none', sm: 'flex' },
                            flexDirection: 'column',
                            gap: 1,
                            maxHeight: 'calc(100vh - 48px)',
                            overflowY: 'auto',
                            overflowX: 'visible',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                        }}
                    >
                        {!hideUnmatchedAndVacantAlerts && <VacantPropertiesAlert />}
                        <NotificationAlert />
                    </Box>
                </>
            )}
        </>
    );
}

export default function LandlordLayout({ isLoading, children }: Props) {
    return (
        <LandlordLayoutProvider>
            <LandlordLayoutContent isLoading={isLoading}>
                {children}
            </LandlordLayoutContent>
        </LandlordLayoutProvider>
    );
}
